import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, MapPin } from 'lucide-react';
import { categoriesAPI } from '../services/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Hero = () => {
  const [service, setService] = useState('');
  const [location, setLocation] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [providerStats, setProviderStats] = useState(null);
  const navigate = useNavigate();

  // List of major Canadian cities and postal code prefixes for autocomplete
  const canadianLocations = [
    // Major cities
    { display: 'Toronto, ON', searchTerms: ['toronto', 'to', 'M'] },
    { display: 'Montreal, QC', searchTerms: ['montreal', 'mtl', 'H'] },
    { display: 'Vancouver, BC', searchTerms: ['vancouver', 'van', 'V'] },
    { display: 'Calgary, AB', searchTerms: ['calgary', 'T2', 'T3'] },
    { display: 'Edmonton, AB', searchTerms: ['edmonton', 'T5', 'T6'] },
    { display: 'Ottawa, ON', searchTerms: ['ottawa', 'K1', 'K2'] },
    { display: 'Winnipeg, MB', searchTerms: ['winnipeg', 'R'] },
    { display: 'Quebec City, QC', searchTerms: ['quebec city', 'G1'] },
    { display: 'Hamilton, ON', searchTerms: ['hamilton', 'L8', 'L9'] },
    { display: 'Kitchener, ON', searchTerms: ['kitchener', 'N2'] },
    { display: 'London, ON', searchTerms: ['london', 'N5', 'N6'] },
    { display: 'Victoria, BC', searchTerms: ['victoria', 'V8', 'V9'] },
    { display: 'Halifax, NS', searchTerms: ['halifax', 'B3'] },
    { display: 'Oshawa, ON', searchTerms: ['oshawa', 'L1'] },
    { display: 'Windsor, ON', searchTerms: ['windsor', 'N8', 'N9'] },
    { display: 'Saskatoon, SK', searchTerms: ['saskatoon', 'S7'] },
    { display: 'Regina, SK', searchTerms: ['regina', 'S4'] },
    { display: 'St. John\'s, NL', searchTerms: ['st johns', 'stjohns', 'A1'] },
    { display: 'Barrie, ON', searchTerms: ['barrie', 'L4'] },
    { display: 'Kelowna, BC', searchTerms: ['kelowna', 'V1'] },
    { display: 'Abbotsford, BC', searchTerms: ['abbotsford', 'V2'] },
    { display: 'Sudbury, ON', searchTerms: ['sudbury', 'P3'] },
    { display: 'Kingston, ON', searchTerms: ['kingston', 'K7'] },
    { display: 'Saguenay, QC', searchTerms: ['saguenay', 'G7'] },
    { display: 'Sherbrooke, QC', searchTerms: ['sherbrooke', 'J1'] },
    { display: 'Guelph, ON', searchTerms: ['guelph', 'N1'] },
    { display: 'Moncton, NB', searchTerms: ['moncton', 'E1'] },
    { display: 'Brantford, ON', searchTerms: ['brantford', 'N3'] },
    { display: 'Thunder Bay, ON', searchTerms: ['thunder bay', 'P7'] },
    { display: 'Red Deer, AB', searchTerms: ['red deer', 'T4'] }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesData, statsData] = await Promise.all([
          categoriesAPI.getAll(),
          axios.get(`${API_URL}/api/providers/stats/overview`)
        ]);
        setCategories(categoriesData);
        setProviderStats(statsData.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Handle location input change with autocomplete (city names and postal codes)
  const handleLocationChange = (e) => {
    const value = e.target.value.toUpperCase(); // Postal codes are uppercase
    setLocation(value);
    
    if (value.length > 0) {
      const searchValue = value.toLowerCase().replace(/\s/g, ''); // Remove spaces for matching
      
      const matches = canadianLocations.filter(location =>
        // Match city name or any postal code prefix
        location.searchTerms.some(term => 
          term.toLowerCase().startsWith(searchValue) ||
          searchValue.startsWith(term.toLowerCase())
        )
      ).map(loc => loc.display);
      
      setLocationSuggestions(matches.slice(0, 5)); // Show top 5 matches
      setShowLocationSuggestions(matches.length > 0);
    } else {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
    }
  };

  const selectLocation = (city) => {
    setLocation(city);
    setShowLocationSuggestions(false);
  };

  const filteredCategories = service
    ? categories.filter(cat =>
        cat.name.toLowerCase().includes(service.toLowerCase())
      )
    : [];

  const handleSearch = () => {
    navigate('/providers', { state: { service, location } });
  };

  const handleServiceSelect = (categoryName) => {
    setService(categoryName);
    setShowSuggestions(false);
  };

  return (
    <div className="relative bg-gradient-to-br from-gray-50 to-orange-50 overflow-hidden">
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1629470385291-ca09537a0563)',
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50"></div>
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div className="max-w-3xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
            Find trusted local pros for any home project
          </h1>
          <p className="text-xl text-gray-200 mb-8">
            Connect with top-rated service professionals across Canada
          </p>

          {/* Search Box */}
          <div className="bg-white rounded-lg shadow-2xl p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="text"
                    placeholder="What service do you need?"
                    value={service}
                    onChange={(e) => {
                      setService(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    className="pl-10 h-12 text-base"
                  />
                </div>
                {/* Service Suggestions */}
                {showSuggestions && service && filteredCategories.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto z-10">
                    {filteredCategories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleServiceSelect(cat.name)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{cat.name}</div>
                        <div className="text-sm text-gray-500">{cat.description}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="text"
                    placeholder="Start typing your city..."
                    value={location}
                    onChange={handleLocationChange}
                    onFocus={() => location && setShowLocationSuggestions(locationSuggestions.length > 0)}
                    onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
                    className="pl-10 h-12 text-base"
                  />
                  {/* Location Suggestions */}
                  {showLocationSuggestions && locationSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto z-10">
                      {locationSuggestions.map((city, index) => (
                        <button
                          key={index}
                          onClick={() => selectLocation(city)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 flex items-center"
                        >
                          <MapPin className="h-4 w-4 text-orange-600 mr-2" />
                          <span className="text-gray-900">{city}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <Button
                onClick={handleSearch}
                className="bg-orange-600 hover:bg-orange-700 text-white font-semibold h-12 px-8 text-base"
              >
                Search
              </Button>
            </div>
          </div>

          {/* Popular Searches */}
          <div className="mt-6">
            <p className="text-gray-200 text-sm mb-3">Popular: </p>
            <div className="flex flex-wrap gap-2">
              {['Plumbing', 'Electrical', 'Snow Removal', 'HVAC', 'Landscaping'].map((item) => (
                <button
                  key={item}
                  onClick={() => {
                    setService(item);
                    setShowSuggestions(false);
                  }}
                  className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium transition-all"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Free for Homeowners Banner */}
      <div className="bg-green-600 text-white py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg sm:text-xl font-bold">
            🎉 100% FREE for Homeowners - Find Local Pros at No Cost! 🎉
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <div className="relative bg-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl sm:text-4xl font-bold mb-1">
                {providerStats ? providerStats.totalProviders.toLocaleString() : '...'}
              </div>
              <div className="text-orange-100 text-sm sm:text-base">Trusted Professionals</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold mb-1">Unlimited</div>
              <div className="text-orange-100 text-sm sm:text-base">Quote Requests</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold mb-1">Top Rated</div>
              <div className="text-orange-100 text-sm sm:text-base">Service Providers</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold mb-1">100%</div>
              <div className="text-orange-100 text-sm sm:text-base">Free to Use</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;